import type { GuessTheNumberStaticContent } from '@core-api/editor/types/response';
import type { Question } from '@mentimeter/legacy-series-types/question';
import type {
  CompatibilityInteractiveContent,
  CompatibilitySlide,
} from '../../compatibility-types';
import { getOrSetCache, type WithCache } from '../../utils/get-or-set-cache';
import type { CommonQuestionProperty } from './get-common-question-property';

export const getQuestionGuessTheNumberProperty = (
  target: CompatibilitySlide,
  prop: keyof Question,
) => {
  const interactiveContent = target.interactiveContents?.[0];
  const staticContent = target.staticContent as GuessTheNumberStaticContent;

  if (prop === 'question_styled') {
    return staticContent.styledTitle;
  }

  if (interactiveContent) {
    switch (prop as keyof Omit<Question, CommonQuestionProperty>) {
      case 'module_custom_data': {
        const moduleCustomData = getModuleCustomData(
          interactiveContent,
          staticContent.interactiveContentStyling,
        );
        const cacheKey = JSON.stringify(moduleCustomData);

        return getOrSetCache(
          target as WithCache<CompatibilitySlide>,
          'module_custom_data',
          cacheKey,
          moduleCustomData,
        );
      }
      case 'range': {
        const range = getRange(
          interactiveContent,
          staticContent.interactiveContentStyling,
        );
        const cacheKey = JSON.stringify(range);

        return getOrSetCache(
          target as WithCache<CompatibilitySlide>,
          'range',
          cacheKey,
          range,
        );
      }
      default:
        return undefined;
    }
  }
};

const DEFAULT_CORRECT_ANSWER_RANGE = {
  min: 7,
  max: 7,
};

const getModuleCustomData = (
  interactiveContent: CompatibilityInteractiveContent,
  interactiveContentStyling: GuessTheNumberStaticContent['interactiveContentStyling'],
) => {
  const choice = interactiveContent.choices?.[0];
  const range = {
    min: choice?.correctAnswerRange?.min ?? DEFAULT_CORRECT_ANSWER_RANGE.min,
    max: choice?.correctAnswerRange?.max ?? DEFAULT_CORRECT_ANSWER_RANGE.max,
  };

  const errorMargin = Math.ceil((range.max - range.min) / 2);
  const correctAnswer = range.min + errorMargin;

  return {
    correct_answer: correctAnswer,
    display_min: interactiveContent?.responseRange?.min,
    error_margin: errorMargin,
    has_error_margin: errorMargin > 0,
    scale: interactiveContentStyling?.responseRangeScale,
  };
};

const getRange = (
  interactiveContent: CompatibilityInteractiveContent,
  interactiveContentStyling: GuessTheNumberStaticContent['interactiveContentStyling'],
) => {
  const responseRange = interactiveContent.responseRange;

  if (!responseRange) {
    return undefined;
  }

  const min = responseRange.min || 0;
  const max = responseRange.max || 0;

  const scale = interactiveContentStyling?.responseRangeScale;

  return {
    min: 0,
    max: Math.ceil((max - min) / scale),
  };
};
